import { Mark, mergeAttributes } from '@tiptap/core'

/**
 * Het anker van een opmerking: een mark op de becommentarieerde tekst (R-73, ontwerp 6.8).
 *
 * Overgenomen uit de CitatenPOC (`src/comments/CommentMark.ts`), waar dit met NWO is afgestemd
 * (eisenboek 12.9), met de namen van dit product erin. De mark draagt **alleen** het draad-id; het
 * gesprek zelf staat los, in de dradenwortel van het sectiedocument. Omdat de mark in het
 * gedeelde Yjs-fragment leeft, schuift, splitst en verdwijnt hij automatisch mee bij gelijktijdig
 * bewerken (R-72): geen positiewiskunde met de hand, en dat is precies waarom het een mark is en
 * geen tekenpositie in een map.
 *
 * **De mark draagt geen label, geen nummer en geen kleur.** Dat is dezelfde regel als bij de
 * draad zelf: kleur wordt deterministisch uit de persoon berekend en nooit bewaard, en een nummer
 * zou afgeleide gegevens in primaire opslag zetten.
 *
 * **`inclusive: false`**: typen ná een becommentarieerd woord valt niet in de markering. Zonder
 * dat groeit een opmerking bij elke aanslag mee met de tekst erachter, en dan gaat ze over iets
 * anders dan waarover ze is gemaakt.
 *
 * **Eén draad-id per mark, net als in de POC.** Twee opmerkingen op exact dezelfde tekst kunnen
 * daarmee niet. Dat is een grens en geen vergissing: overlappende ankers vragen een mark die
 * meerdere id's draagt, en dan is niet meer te zeggen welk anker een lezer aanwijst. Geen eis
 * vraagt het.
 */

export const COMMENT_MARK = 'opmerking'

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    opmerking: {
      /** Markeert de huidige selectie als anker van een opmerkingdraadje. */
      zetOpmerking: (draadId: string) => ReturnType
      /** Haalt de markering uit de selectie. */
      haalOpmerkingWeg: () => ReturnType
    }
  }
}

export const CommentMark = Mark.create({
  name: COMMENT_MARK,
  inclusive: false,

  addAttributes() {
    return {
      draadId: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-draad-id'),
        renderHTML: (attributes) =>
          attributes.draadId === null || attributes.draadId === undefined
            ? {}
            : { 'data-draad-id': attributes.draadId },
      },
    }
  },

  parseHTML() {
    return [{ tag: 'span[data-draad-id]' }]
  },

  renderHTML({ HTMLAttributes }) {
    // Een `span` met een eigen klasse en verder niets: de opmaak staat in `schil.css` en komt uit
    // de tokens, en kleur is er nooit de enige drager (R-139) omdat het paneel de opmerking als
    // tekst toont.
    return ['span', mergeAttributes(HTMLAttributes, { class: 'ef-opmerkinganker' }), 0]
  },

  addCommands() {
    return {
      zetOpmerking:
        (threadId) =>
        ({ commands }) =>
          commands.setMark(this.name, { draadId: threadId }),
      haalOpmerkingWeg:
        () =>
        ({ commands }) =>
          commands.unsetMark(this.name),
    }
  },
})
